/** The audit trail in the reader's words (nine-point round, point 7).
 *
 *  A row like `manual_match · resolution #482` is a database view; the page
 *  a partner reads should say "Priya matched entries in resolution #482".
 *  Known actions get real verbs; anything this build has never heard of
 *  falls back to the action with its underscores spaced — never dropped.
 */

interface SentenceRow {
  action: string
  object_type: string
  object_id: string | null
  user_name: string | null
}

const VERBS: Record<string, string> = {
  login: 'signed in', logout: 'signed out',
  login_failed: 'failed to sign in', refresh: 'refreshed their session',
  approve: 'approved', reject: 'rejected',
  reverse: 'reversed', reverse_request: 'asked to reverse',
  automatch: 'ran auto-match on', manual_match: 'matched entries in',
  explain: 'explained', reject_offer: 'declined an offer on',
  set_grain: 'set the books grain on', upload: 'uploaded',
  settings: 'changed settings for', keep_one: 'kept one of',
  create: 'created', update: 'updated', delete: 'deleted',
  set_expected_section: 'set the expected section on',
  // this build's verbs (compat.audit's tails) beyond the predecessor's set
  created: 'created', updated: 'updated', archived: 'archived',
  retire: 'retired', reinstate: 'reinstated', resolve: 'resolved',
  propose: 'proposed', review: 'reviewed', rerun: 're-ran',
  reparse: 're-parsed', reupload: 're-uploaded',
  accept_offers: 'accepted offers on',
  withdraw_offer_rejection: 'withdrew an offer rejection on',
  reverse_reject: 'refused the reversal of',
  regenerate_documents: 'regenerated the documents of',
  edit_ledger_row: 'corrected a books row on',
  undo_ledger_edit: 'undid a books correction on',
}

export function auditSentence(r: SentenceRow): string {
  const who = r.user_name || 'the system'
  // this build's trail namespaces actions as `<target_kind>.<verb>`
  // (compat.audit) — the verb vocabulary is the predecessor's, after the dot.
  // Found by the P7 container pass: keyed on the whole action, every sentence
  // fell back to "the system resolution.manual match …".
  const tail = r.action.slice(r.action.lastIndexOf('.') + 1)
  const verb = VERBS[tail] ?? tail.replace(/_/g, ' ')
  const obj = r.object_id ? `${r.object_type} #${r.object_id}` : r.object_type
  return `${who} ${verb} ${obj}`
}

/** The day bucket for the "By day" reading; sorting the keys descending
 *  gives newest-day-first, which is how a catch-up reads. */
export function dayOf(iso: string | null): string {
  return iso ? iso.slice(0, 10) : 'undated'
}
